import React, { useState, useEffect } from 'react';
import { 
  Upload, FileText, Table, Users, Target, Play, Download, CheckCircle, Info,
  Check, X, AlertCircle, ChevronRight, Settings, TrendingUp, Link2, ArrowRight,
  Filter, Plus, Edit2, Trash2, Eye, Send, Calendar, RefreshCw, AlertTriangle,
  Lock, Unlock, RotateCcw, ArrowRightLeft, ChevronDown, ChevronUp, Database, Hash
} from 'lucide-react';
import { apiCall } from './Api';
import { VersionColumnMapping } from './VersionComponents';
import ColumnMatchingWarning from './ColumnMatchingWarning';
import { getCurrentToken } from './SessionManager';

// Utility functions for data type formatting
const formatDataType1 = (dtype) => {
  const typeMap = {
    'int64': 'Integer',
    'int32': 'Integer',
    'int': 'Integer',
    'float64': 'Decimal',
    'float32': 'Decimal',
    'float': 'Decimal',
    'string': 'Text',
    'object': 'Text',
    'datetime': 'Date/Time',
    'datetime64': 'Date/Time',
    'bool': 'Boolean'
  };
  return typeMap[dtype] || dtype;
};

const formatDataType = (dtype) => {
  // Remove Nullable wrapper
  const cleanType = dtype.replace(/^Nullable\(/, '').replace(/\)$/, '');
  
  const typeMap = {
    // Integer types
    'UInt8': 'Integer',
    'UInt16': 'Integer',
    'UInt32': 'Integer',
    'UInt64': 'Integer',
    'Int8': 'Integer',
    'Int16': 'Integer',
    'Int32': 'Integer',
    'Int64': 'Integer',
    
    // Float types
    'Float32': 'Decimal',
    'Float64': 'Decimal',
    'Decimal': 'Decimal',
    'Decimal32': 'Decimal',
    'Decimal64': 'Decimal',
    'Decimal128': 'Decimal',
    
    // String types
    'String': 'Text',
    'FixedString': 'Text',
    
    // Date/Time types
    'Date': 'Date/Time',
    'Date32': 'Date/Time',
    'DateTime': 'Date/Time',
    'DateTime64': 'Date/Time',
    
    // Boolean
    'Bool': 'Boolean',
    
    // Array/Complex
    'Array': 'Array',
    
    // Enum
    'Enum8': 'Text',
    'Enum16': 'Text'
  };
  
  // Check for base type (handles Array(String), Decimal(10,2), etc.)
  const baseType = cleanType.split('(')[0];
  
  return typeMap[baseType] || typeMap[cleanType] || cleanType;
};

const getTypeColor = (dtype) => {
  const colorMap = {
    'int64': 'text-blue-600',
    'int32': 'text-blue-600',
    'int': 'text-blue-600',
    'float64': 'text-purple-600',
    'float32': 'text-purple-600',
    'float': 'text-purple-600',
    'string': 'text-gray-600',
    'object': 'text-gray-600',
    'datetime': 'text-green-600',
    'datetime64': 'text-green-600',
    'bool': 'text-orange-600'
  };
  return colorMap[dtype] || 'text-gray-600';
};

// Mapping Editor Modal Component
const MappingEditorModal = ({ targetColumn, mappings, files, allTargetColumns, onClose, onSave }) => {
  const [editedMappings, setEditedMappings] = useState([...mappings]);
  const [newTargetColumn, setNewTargetColumn] = useState(targetColumn);

  const handleSave = () => {
    // Update target column for all mappings
    const updated = editedMappings.map(m => ({
      ...m,
      target_column: newTargetColumn
    }));
    onSave(updated);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">Edit Column Mapping</h2>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Target Column Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Column Name
            </label>
            <input
              type="text"
              value={newTargetColumn}
              onChange={(e) => setNewTargetColumn(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter unified column name"
            />
            <p className="text-xs text-gray-500 mt-1">
              This is the name that will be used in the unified table
            </p>
          </div>

          {/* Source Mappings */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Source Columns
            </label>
            <div className="space-y-2">
              {editedMappings.map((mapping, idx) => {
                const file = files.find(f => f.file_id === mapping.source_file_id);
                return (
                  <div key={idx} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <Link2 className="h-4 w-4 text-gray-400" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{file?.filename}</p>
                      <p className="text-xs text-gray-600">{mapping.source_column}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                    <div className="text-sm text-gray-900 font-medium">{newTargetColumn}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== Unified Schema View (Post-Unification) ====================
// Fetches live schema from /unified-schema — single source of truth

const TYPE_CATEGORY_COLORS = {
  text: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Text' },
  number: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Integer' },
  decimal: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Decimal' },
  date: { bg: 'bg-green-100', text: 'text-green-700', label: 'Date' },
  datetime: { bg: 'bg-green-100', text: 'text-green-700', label: 'Date/Time' },
  boolean: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Boolean' },
  array: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Array' },
  other: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Other' },
};

const UnifiedSchemaView = ({ session, files }) => {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCol, setExpandedCol] = useState(null);
  
  // Type change state
  const [typeChangeCol, setTypeChangeCol] = useState(null);
  const [typeChangeTarget, setTypeChangeTarget] = useState('');
  const [typeCheckResult, setTypeCheckResult] = useState(null);
  const [typeChecking, setTypeChecking] = useState(false);
  const [typeChanging, setTypeChanging] = useState(false);
  const [typeChangeSuccess, setTypeChangeSuccess] = useState(null);

  useEffect(() => {
    loadSchema();
  }, [session?.session_id]);

  const loadSchema = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getCurrentToken();
      const response = await apiCall(
        `/api/cdp/sessions/${session.session_id}/unified-schema`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        setSchema(await response.json());
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to load schema');
      }
    } catch (e) {
      setError('Network error: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const checkTypeChange = async (colName, targetType) => {
    setTypeChecking(true);
    setTypeCheckResult(null);
    setTypeChangeSuccess(null);
    try {
      const token = getCurrentToken();
      const response = await apiCall(
        `/api/cdp/sessions/${session.session_id}/columns/${colName}/check-type-change`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ target_type: targetType })
        }
      );
      if (response.ok) {
        setTypeCheckResult(await response.json());
      }
    } catch (e) {
      console.error('Check error:', e);
    } finally {
      setTypeChecking(false);
    }
  };

  const executeTypeChange = async (colName, targetType, force = false) => {
    setTypeChanging(true);
    try {
      const token = getCurrentToken();
      const response = await apiCall(
        `/api/cdp/sessions/${session.session_id}/columns/${colName}/change-type`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ target_type: targetType, force })
        }
      );
      if (response.ok) {
        const result = await response.json();
        setTypeChangeSuccess(result);
        // Reload schema to reflect the change
        setTimeout(() => {
          setTypeChangeCol(null);
          setTypeCheckResult(null);
          setTypeChangeTarget('');
          setTypeChangeSuccess(null);
          loadSchema();
        }, 2000);
      } else {
        const err = await response.json();
        alert(typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail));
      }
    } catch (e) {
      alert('Failed: ' + e.message);
    } finally {
      setTypeChanging(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-12 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-600">Loading unified schema...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center space-x-2 text-red-700">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
        <button onClick={loadSchema} className="mt-3 text-sm text-red-600 hover:underline">Retry</button>
      </div>
    );
  }

  if (!schema) return null;

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-6 py-4 border-b bg-green-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Data Unified Successfully</h2>
              <p className="text-sm text-gray-600 mt-1">
                Unified on {schema.unified_at ? new Date(schema.unified_at).toLocaleString() : 'N/A'} • {schema.total_rows?.toLocaleString()} rows • {schema.total_columns} columns • Version {schema.current_version}
                {schema.mapping_created_by && <span> • by {schema.mapping_created_by}</span>}
              </p>
            </div>
          </div>
          <button onClick={loadSchema} className="p-2 hover:bg-green-100 rounded-lg transition-colors" title="Refresh schema">
            <RefreshCw className="h-4 w-4 text-green-600" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="px-6 py-3 bg-gray-50 border-b">
        <div className="grid grid-cols-5 gap-4">
          <div>
            <p className="text-xs text-gray-500">Columns</p>
            <p className="text-lg font-bold text-gray-900">{schema.total_columns}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Dedup Keys</p>
            <p className="text-lg font-bold text-purple-600">{schema.deduplication_keys?.length || 0}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Merge Strategy</p>
            <p className="text-sm font-medium text-gray-700 mt-1">{schema.merge_strategy || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Total Rows</p>
            <p className="text-lg font-bold text-gray-900">{schema.total_rows?.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Source Files</p>
            <p className="text-lg font-bold text-gray-900">
              {new Set(schema.columns.flatMap(c => c.source_mappings.map(m => m.source_file_id))).size}
            </p>
          </div>
        </div>
      </div>

      {/* Column Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Column</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Live Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source Files</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flags</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {schema.columns.map((col) => {
              const catStyle = TYPE_CATEGORY_COLORS[col.category] || TYPE_CATEGORY_COLORS.other;
              const isExpanded = expandedCol === col.name;
              const isTypeChanging = typeChangeCol === col.name;
              
              return (
                <React.Fragment key={col.name}>
                  <tr className={`hover:bg-gray-50 ${isExpanded ? 'bg-blue-50/30' : ''}`}>
                    {/* Expand toggle */}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setExpandedCol(isExpanded ? null : col.name)}
                        className="p-0.5 text-gray-400 hover:text-gray-600"
                      >
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                    </td>
                    
                    {/* Column name */}
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{col.name}</span>
                    </td>
                    
                    {/* Live type from ClickHouse */}
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${catStyle.bg} ${catStyle.text}`}>
                          {catStyle.label}
                        </span>
                        <span className="text-xs text-gray-400 font-mono">{col.base_type}</span>
                      </div>
                    </td>
                    
                    {/* Source files summary */}
                    <td className="px-4 py-3 text-sm">
                      {col.source_mappings.length > 0 ? (
                        <div className="space-y-0.5">
                          {col.source_mappings.slice(0, 2).map((m, idx) => (
                            <div key={idx} className="flex items-center space-x-1.5 text-xs">
                              <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                              <span className="text-gray-600 truncate max-w-[200px]">
                                {m.filename}: <span className="font-medium">{m.source_column}</span>
                              </span>
                            </div>
                          ))}
                          {col.source_mappings.length > 2 && (
                            <span className="text-xs text-gray-400">+{col.source_mappings.length - 2} more</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">System / API column</span>
                      )}
                    </td>
                    
                    {/* Flags */}
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-1.5">
                        {col.is_dedup_key && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Key</span>
                        )}
                        {col.is_indexed && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Indexed</span>
                        )}
                        {col.is_nullable && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] text-gray-400">Nullable</span>
                        )}
                      </div>
                    </td>
                    
                    {/* Actions */}
                    <td className="px-4 py-3">
                      {col.safe_type_conversions.length > 0 && !col.is_dedup_key && (
                        <button
                          onClick={() => {
                            setTypeChangeCol(isTypeChanging ? null : col.name);
                            setTypeCheckResult(null);
                            setTypeChangeTarget('');
                            setTypeChangeSuccess(null);
                          }}
                          className={`p-1.5 rounded transition-colors ${
                            isTypeChanging ? 'bg-blue-100 text-blue-700' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                          }`}
                          title="Change column type"
                        >
                          <ArrowRightLeft className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                  
                  {/* Expanded detail row */}
                  {isExpanded && (
                    <tr>
                      <td colSpan={6} className="px-8 py-4 bg-gray-50 border-b">
                        <div className="grid grid-cols-2 gap-6">
                          {/* Source mappings detail */}
                          <div>
                            <h4 className="text-xs font-semibold text-gray-700 uppercase mb-2">Source Mappings</h4>
                            {col.source_mappings.length > 0 ? (
                              <div className="space-y-2">
                                {col.source_mappings.map((m, idx) => (
                                  <div key={idx} className="flex items-center space-x-3 text-sm bg-white rounded p-2 border">
                                    <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-gray-900 font-medium text-xs truncate">{m.filename}</p>
                                      <p className="text-gray-500 text-xs">
                                        Column: <span className="font-mono">{m.source_column}</span>
                                        {m.original_data_type && (
                                          <span className="ml-2 text-gray-400">({m.original_data_type})</span>
                                        )}
                                      </p>
                                    </div>
                                    <ArrowRight className="h-3 w-3 text-gray-300" />
                                    <span className="font-mono text-xs text-gray-700">{col.name}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-400">No file mappings — column may come from API sources or was added by the system.</p>
                            )}
                          </div>
                          
                          {/* Column metadata */}
                          <div>
                            <h4 className="text-xs font-semibold text-gray-700 uppercase mb-2">Column Details</h4>
                            <div className="space-y-1.5 text-xs">
                              <div className="flex justify-between">
                                <span className="text-gray-500">ClickHouse Type</span>
                                <span className="font-mono text-gray-800">{col.clickhouse_type}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Category</span>
                                <span className={`font-medium ${catStyle.text}`}>{catStyle.label}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Dedup Key</span>
                                <span>{col.is_dedup_key ? 'Yes' : 'No'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Indexed</span>
                                <span>{col.is_indexed ? `Yes (${col.index_name})` : 'No'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Query Hint</span>
                                <code className="text-gray-500 truncate max-w-[200px]">{col.hint}</code>
                              </div>
                              {col.safe_type_conversions.length > 0 && (
                                <div className="pt-2 border-t mt-2">
                                  <span className="text-gray-500">Can convert to:</span>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {col.safe_type_conversions.map((conv, i) => (
                                      <span key={i} className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px]" title={conv.risk}>
                                        {conv.label}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  
                  {/* Type change panel */}
                  {isTypeChanging && (
                    <tr>
                      <td colSpan={6} className="px-8 py-4 bg-blue-50 border-b">
                        <TypeChangePanel
                          col={col}
                          typeChangeTarget={typeChangeTarget}
                          setTypeChangeTarget={setTypeChangeTarget}
                          typeCheckResult={typeCheckResult}
                          typeChecking={typeChecking}
                          typeChanging={typeChanging}
                          typeChangeSuccess={typeChangeSuccess}
                          onCheck={(target) => checkTypeChange(col.name, target)}
                          onExecute={(target, force) => executeTypeChange(col.name, target, force)}
                          onCancel={() => { setTypeChangeCol(null); setTypeCheckResult(null); }}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Info box */}
      <div className="px-6 py-4 border-t">
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Column Mappings & Types</p>
              <p>Mappings are locked after unification. You can change column data types if the conversion doesn't lose data — 
              click the <ArrowRightLeft className="h-3.5 w-3.5 inline" /> button on any column to check.
              Expand a row to see full source mapping details.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


// Type Change Panel sub-component
const TypeChangePanel = ({
  col, typeChangeTarget, setTypeChangeTarget,
  typeCheckResult, typeChecking, typeChanging, typeChangeSuccess,
  onCheck, onExecute, onCancel
}) => {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900">
          Change Type: <span className="font-mono">{col.name}</span>
          <span className="ml-2 text-xs text-gray-500 font-normal">Currently: {col.base_type}</span>
        </h4>
        <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600">
          <X className="h-4 w-4" />
        </button>
      </div>
      
      {typeChangeSuccess ? (
        <div className="flex items-center space-x-2 p-3 bg-green-100 rounded-lg text-sm text-green-800">
          <CheckCircle className="h-4 w-4" />
          <span>
            Changed <strong>{col.name}</strong> from {typeChangeSuccess.previous_type} to {typeChangeSuccess.new_type}.
            {typeChangeSuccess.rows_nullified > 0 && ` ${typeChangeSuccess.rows_nullified} values set to NULL.`}
          </span>
        </div>
      ) : (
        <>
          <div className="flex items-center space-x-3">
            <select
              value={typeChangeTarget}
              onChange={(e) => setTypeChangeTarget(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select target type...</option>
              {col.safe_type_conversions.map((conv, i) => (
                <option key={i} value={conv.type}>{conv.label} ({conv.type}) — {conv.risk}</option>
              ))}
            </select>
            
            <button
              onClick={() => onCheck(typeChangeTarget)}
              disabled={!typeChangeTarget || typeChecking}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              {typeChecking ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
              <span>{typeChecking ? 'Checking...' : 'Check Safety'}</span>
            </button>
          </div>
          
          {/* Check result */}
          {typeCheckResult && (
            <div className={`p-4 rounded-lg border ${typeCheckResult.safe ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-start space-x-3">
                {typeCheckResult.safe ? (
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${typeCheckResult.safe ? 'text-green-800' : 'text-red-800'}`}>
                    {typeCheckResult.reason}
                  </p>
                  <div className="mt-2 grid grid-cols-3 gap-4 text-xs">
                    <div>
                      <span className="text-gray-500">Total rows</span>
                      <p className="font-semibold">{typeCheckResult.total_rows?.toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Will convert</span>
                      <p className="font-semibold text-green-700">{typeCheckResult.convertible_rows?.toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Will fail</span>
                      <p className={`font-semibold ${typeCheckResult.failed_rows > 0 ? 'text-red-700' : 'text-gray-500'}`}>
                        {typeCheckResult.failed_rows?.toLocaleString()}
                        {typeCheckResult.loss_percentage > 0 && ` (${typeCheckResult.loss_percentage}%)`}
                      </p>
                    </div>
                  </div>
                  
                  {/* Sample failures */}
                  {typeCheckResult.sample_failures?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-red-700 mb-1">Sample values that can't convert:</p>
                      <div className="flex flex-wrap gap-1">
                        {typeCheckResult.sample_failures.map((val, i) => (
                          <code key={i} className="px-1.5 py-0.5 bg-red-100 rounded text-xs text-red-800">{val}</code>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Action buttons */}
                  <div className="mt-3 flex items-center space-x-2">
                    {typeCheckResult.safe ? (
                      <button
                        onClick={() => onExecute(typeChangeTarget, false)}
                        disabled={typeChanging}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                      >
                        {typeChanging ? 'Converting...' : `Convert to ${typeChangeTarget}`}
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (window.confirm(
                            `This will set ${typeCheckResult.failed_rows} values to NULL. Are you sure?`
                          )) {
                            onExecute(typeChangeTarget, true);
                          }
                        }}
                        disabled={typeChanging}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
                      >
                        {typeChanging ? 'Converting...' : `Force Convert (${typeCheckResult.failed_rows} values → NULL)`}
                      </button>
                    )}
                    <button
                      onClick={onCancel}
                      className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};


const EnhancedUnifyTab = ({ session, files, onRefresh }) => {
  const [columnMappings, setColumnMappings] = useState([]);
  const [deduplicationKeys, setDeduplicationKeys] = useState([]);
  const [indexableColumns, setIndexableColumns] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [columnAnalysis, setColumnAnalysis] = useState(null);
  const [showMappingEditor, setShowMappingEditor] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [columnDataTypes, setColumnDataTypes] = useState({}); // Store selected data type for each target column
  
  // Version-aware state
  const isVersioned = session?.current_version > 0;
  const nextVersion = session?.current_version + 1 || 1;
  const hasUnprocessedFiles = files.some(f => f.version === nextVersion);
  const isVersionUpdate = isVersioned && hasUnprocessedFiles;
  
  // Locked keys from existing unified table (for versioned updates)
  const [lockedDeduplicationKeys, setLockedDeduplicationKeys] = useState([]);
  const [lockedIndexableColumns, setLockedIndexableColumns] = useState([]);

  useEffect(() => {
    if (files.length > 0) {
      analyzeColumns();
    }
  }, [files]);

  const analyzeColumns = async () => {
    try {
      setAnalyzing(true);
      const token = getCurrentToken();
      
      // Use different endpoint based on whether this is a version update
      const endpoint = `/api/cdp/sessions/${session.session_id}/analyze-columns`;
      
      const requestBody = isVersionUpdate
        ? JSON.stringify({ version_number: nextVersion })
        : undefined;
      
      const response = await apiCall(endpoint, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (response.ok) {
        const analysis = await response.json();
        setColumnAnalysis(analysis);
        
        // For version updates, lock existing deduplication keys and indexes
        if (isVersionUpdate && analysis.existing_schema) {
          setLockedDeduplicationKeys(analysis.existing_schema.deduplication_keys || []);
          setLockedIndexableColumns(analysis.existing_schema.indexed_columns || []);
          setDeduplicationKeys(analysis.existing_schema.deduplication_keys || []);
          setIndexableColumns(analysis.existing_schema.indexed_columns || []);
        }
        
        // Initialize column mappings based on analysis
        initializeColumnMappingsFromAnalysis(analysis);
      }
    } catch (error) {
      console.error('Error analyzing columns:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const initializeColumnMappingsFromAnalysis = (analysis) => {
    const mappings = [];
    let indexCounter = 0;
    
    // Add exact matches
    if (analysis.exact_matches) {
      Object.entries(analysis.exact_matches).forEach(([colName, fileIds]) => {
        // Get the first file to determine data type
        const firstFileId = fileIds[0];
        const firstFile = files.find(f => f.file_id === firstFileId);
        let colMetadata = firstFile?.column_dict?.find(c => c.name === colName)?.data_type;
        if(analysis.column_dict[colName]) {
          colMetadata = analysis.column_dict[colName];
        }
        
        fileIds.forEach(fileId => {
          mappings.push({
            source_file_id: fileId,
            source_column: colName,
            target_column: colName,
            data_type: colMetadata || 'string',
            is_key: false,
            is_indexable: false,
            match_type: 'exact',
            confidence: 1.0,
            originalIndex: indexCounter++,
            removed: false
          });
        });
      });
    }
    
    // Add auto-matched columns (for version updates)
    if (analysis.auto_matched) {
      analysis.auto_matched.forEach(match => {
        const file = files.find(f => f.file_id === match.file_id);
        //const colMetadata = file?.columns_metadata?.find(c => c.name === match.source_column);
        let colMetadata = file?.column_dict?.find(c => c.name === match.source_column)?.data_type;
        if(analysis.column_dict[match.source_column]) {
          colMetadata = analysis.column_dict[match.source_column];
        }
        
        mappings.push({
          source_file_id: match.file_id,
          source_column: match.source_column,
          target_column: match.target_column,
          data_type: colMetadata || match.data_type || 'string',
          is_key: lockedDeduplicationKeys.includes(match.target_column),
          is_indexable: lockedIndexableColumns.includes(match.target_column),
          match_type: 'exact',
          confidence: 1.0,
          originalIndex: indexCounter++,
          removed: false
        });
      });
    }
    
    // Add suggested fuzzy matches (user can accept/reject these)
    if (analysis.suggestions) {
      analysis.suggestions.forEach(suggestion => {
        const file = files.find(f => f.file_id === suggestion.source_file_id);
        //const colMetadata = file?.columns_metadata?.find(c => c.name === suggestion.source_column);
        let colMetadata = file?.column_dict?.find(c => c.name === suggestion.source_column)?.data_type;
        if(analysis.column_dict[suggestion.source_column]) {
          colMetadata = analysis.column_dict[suggestion.source_column];
        }

        mappings.push({
          source_file_id: suggestion.source_file_id,
          source_column: suggestion.source_column,
          target_column: suggestion.target_column,
          data_type: colMetadata || 'string',
          is_key: false,
          is_indexable: false,
          match_type: 'fuzzy',
          confidence: suggestion.confidence,
          suggested: true,
          accepted: false,
          originalIndex: indexCounter++,
          removed: false
        });
      });
    }
    
    // Add columns needing review (for version updates)
    if (analysis.needs_mapping) {
      analysis.needs_mapping.forEach(item => {
        const file = files.find(f => f.file_id === item.file_id);
        //const colMetadata = file?.columns_metadata?.find(c => c.name === item.source_column);
        let colMetadata = file?.column_dict?.find(c => c.name === item.source_column)?.data_type;
        if(analysis.column_dict[item.source_column]) {
          colMetadata = analysis.column_dict[item.source_column];
        }
        
        mappings.push({
          source_file_id: item.file_id,
          source_column: item.source_column,
          target_column: item.source_column, // Default to same name
          data_type: colMetadata || item.data_type || 'string',
          is_key: false,
          is_indexable: false,
          match_type: 'unique',
          confidence: 1.0,
          suggested: true,
          accepted: false,
          suggested_matches: item.suggested_matches,
          originalIndex: indexCounter++,
          removed: false
        });
      });
    }
    
    // Add unique columns (unmapped)
    if (analysis.unique_columns) {
      Object.entries(analysis.unique_columns).forEach(([fileId, data]) => {
        const file = files.find(f => f.file_id === fileId);
        data.columns.forEach(colName => {
          //const colMetadata = file?.columns_metadata?.find(c => c.name === colName);
          let colMetadata = file?.column_dict?.find(c => c.name === colName)?.data_type;
          if(analysis.column_dict[colName]) {
            colMetadata = analysis.column_dict[colName];
          }
          mappings.push({
            source_file_id: fileId,
            source_column: colName,
            target_column: colName, // Default to same name
            data_type: colMetadata || 'string',
            is_key: false,
            is_indexable: false,
            match_type: 'unique',
            confidence: 1.0,
            originalIndex: indexCounter++,
            removed: false
          });
        });
      });
    }
    
    // Add new columns (for version updates)
    if (analysis.new_columns) {
      analysis.new_columns.forEach(col => {
        const file = files.find(f => f.file_id === col.file_id);
        mappings.push({
          source_file_id: file?.file_id,
          source_column: col.column_name,
          target_column: col.column_name,
          data_type: col.data_type || 'string',
          is_key: false,
          is_indexable: false,
          match_type: 'new',
          confidence: 1.0,
          originalIndex: indexCounter++,
          removed: false
        });
      });
    }
    
    // Ensure fuzzy suggestion target columns have their own source mapping.
    // The backend may not include a column in exact_matches or unique_columns if it
    // only appears in one file AND has a fuzzy suggestion pointing to it as a target.
    // Without this, rejecting the suggestion would make the target column disappear.
    if (analysis.suggestions && analysis.files) {
      const existingTargets = new Set(
        mappings.filter(m => m.match_type !== 'fuzzy').map(m => m.target_column)
      );
      
      analysis.suggestions.forEach(suggestion => {
        if (!existingTargets.has(suggestion.target_column)) {
          // Find which file actually owns this target column
          const ownerFile = analysis.files.find(f => 
            f.columns && f.columns.includes(suggestion.target_column)
          );
          if (ownerFile) {
            let colMetadata = 'string';
            if (analysis.column_dict[suggestion.target_column]) {
              colMetadata = analysis.column_dict[suggestion.target_column];
            }
            mappings.push({
              source_file_id: ownerFile.file_id,
              source_column: suggestion.target_column,
              target_column: suggestion.target_column,
              data_type: colMetadata,
              is_key: false,
              is_indexable: false,
              match_type: 'exact',
              confidence: 1.0,
              originalIndex: indexCounter++,
              removed: false
            });
            existingTargets.add(suggestion.target_column);
          }
        }
      });
    }

    setColumnMappings(mappings);
    
    // Initialize data types for each target column using backend inference
    const dataTypes = {};
    const inferredTypes = analysis.column_types || {};
    const groupedByTarget = mappings.reduce((acc, mapping) => {
      const key = mapping.match_type === 'rejected' ? mapping.source_column : mapping.target_column;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(mapping);
      return acc;
    }, {});
    
    // Map backend types to dropdown values (int64, float64, datetime64, bool, string)
    const normalizeType = (t) => {
      if (!t) return 'string';
      if (t === 'int64' || t === 'Int64' || t === 'UInt64' || t.startsWith('Int') || t.startsWith('UInt')) return 'int64';
      if (t === 'float64' || t === 'Float64' || t.startsWith('Float') || t.startsWith('Decimal')) return 'float64';
      if (t === 'datetime' || t === 'datetime64' || t === 'DateTime' || t === 'Date' || t.startsWith('DateTime')) return 'datetime64';
      if (t === 'bool' || t === 'Boolean') return 'bool';
      return 'string';
    };

    Object.keys(groupedByTarget).forEach(targetCol => {
      const mappingsForCol = groupedByTarget[targetCol];
      
      // Priority: 1) backend inferred type  2) mapping data_type  3) 'string'
      const backendType = inferredTypes[targetCol];
      const mappingTypes = mappingsForCol.map(m => m.data_type).filter(Boolean);
      
      let bestType;
      if (backendType && backendType !== 'string') {
        bestType = normalizeType(backendType);
      } else if (mappingTypes.length > 0 && mappingTypes[0] !== 'string') {
        bestType = normalizeType(mappingTypes[0]);
      } else {
        bestType = 'string';
      }
      
      dataTypes[targetCol] = {
        selected: bestType,
        suggested: bestType
      };
    });
    
    setColumnDataTypes(dataTypes);
  };

  const acceptSuggestedMapping = (index) => {
    const updated = [...columnMappings];
    updated[index].accepted = true;
    updated[index].suggested = false;
    setColumnMappings(updated);
  };

  const rejectSuggestedMapping = (index) => {
    const updated = [...columnMappings];
    const mapping = updated[index];
    
    mapping.match_type = 'rejected';
    mapping.suggested = false;
    mapping.accepted = false;
    
    setColumnMappings(updated);
    
    // Look up inferred type from backend analysis
    const normalizeType = (t) => {
      if (!t) return 'string';
      if (t === 'int64' || t === 'Int64' || t === 'UInt64' || t.startsWith('Int') || t.startsWith('UInt')) return 'int64';
      if (t === 'float64' || t === 'Float64' || t.startsWith('Float') || t.startsWith('Decimal')) return 'float64';
      if (t === 'datetime' || t === 'datetime64' || t === 'DateTime' || t === 'Date' || t.startsWith('DateTime')) return 'datetime64';
      if (t === 'bool' || t === 'Boolean') return 'bool';
      return 'string';
    };
    const inferredType = columnAnalysis?.column_types?.[mapping.source_column];
    const bestType = inferredType ? normalizeType(inferredType) : normalizeType(mapping.data_type);
    
    // Initialize data type entry for the source column (which becomes a standalone column)
    setColumnDataTypes(prev => ({
      ...prev,
      [mapping.source_column]: prev[mapping.source_column] || {
        selected: bestType,
        suggested: bestType
      }
    }));
  };

  const removeMappingFromGroup = (index) => {
    const updated = [...columnMappings];
    const mapping = updated[index];
    
    // Store original target column before removing
    if (!mapping.originalTargetColumn) {
      mapping.originalTargetColumn = mapping.target_column;
    }
    
    // Mark as removed and set target to source column (creates new row)
    mapping.removed = true;
    mapping.target_column = mapping.source_column;
    mapping.match_type = 'unique';
    mapping.suggested = false;
    mapping.accepted = false;
    
    setColumnMappings(updated);
    
    // Ensure data type entry exists for the now-standalone column
    const normalizeType = (t) => {
      if (!t) return 'string';
      if (t === 'int64' || t === 'Int64' || t === 'UInt64' || t.startsWith('Int') || t.startsWith('UInt')) return 'int64';
      if (t === 'float64' || t === 'Float64' || t.startsWith('Float') || t.startsWith('Decimal')) return 'float64';
      if (t === 'datetime' || t === 'datetime64' || t === 'DateTime' || t === 'Date' || t.startsWith('DateTime')) return 'datetime64';
      if (t === 'bool' || t === 'Boolean') return 'bool';
      return 'string';
    };
    const inferredType = columnAnalysis?.column_types?.[mapping.source_column];
    const bestType = inferredType ? normalizeType(inferredType) : normalizeType(mapping.data_type);
    
    setColumnDataTypes(prev => ({
      ...prev,
      [mapping.source_column]: prev[mapping.source_column] || {
        selected: bestType,
        suggested: bestType
      }
    }));
  };

  const restoreMappingToGroup = (index, targetColumn) => {
    const updated = [...columnMappings];
    const mapping = updated[index];
    
    // Restore to the target group
    mapping.removed = false;
    mapping.target_column = targetColumn;
    mapping.match_type = 'exact';
    mapping.confidence = 1.0;
    
    setColumnMappings(updated);
  };

  const updateColumnMapping = (index, field, value) => {
    const updated = [...columnMappings];
    updated[index][field] = value;
    setColumnMappings(updated);
  };

  const toggleDeduplicationKey = (targetColumn) => {
    // Prevent toggling locked keys in version updates
    if (isVersionUpdate && lockedDeduplicationKeys.includes(targetColumn)) {
      return;
    }
    
    setDeduplicationKeys(prev =>
      prev.includes(targetColumn)
        ? prev.filter(k => k !== targetColumn)
        : [...prev, targetColumn]
    );
  };

  const toggleIndexableColumn = (targetColumn) => {
    // Prevent toggling locked indexes in version updates
    if (isVersionUpdate && lockedIndexableColumns.includes(targetColumn)) {
      return;
    }
    
    setIndexableColumns(prev =>
      prev.includes(targetColumn)
        ? prev.filter(k => k !== targetColumn)
        : [...prev, targetColumn]
    );
  };

  // Available data types for the dropdown
  const availableDataTypes = [
    { value: 'string', label: 'Text', color: 'text-gray-600' },
    { value: 'int64', label: 'Integer', color: 'text-blue-600' },
    { value: 'float64', label: 'Decimal', color: 'text-purple-600' },
    { value: 'datetime64', label: 'Date/Time', color: 'text-green-600' },
    { value: 'bool', label: 'Boolean', color: 'text-orange-600' }
  ];

  const handleDataTypeChange = (targetColumn, newType) => {
    setColumnDataTypes(prev => ({
      ...prev,
      [targetColumn]: {
        ...prev[targetColumn],
        selected: newType
      }
    }));
  };

  const handleUnifyData = async () => {
    try {
      setProcessing(true);
      const token = getCurrentToken();

      // Filter out pending suggestions (not yet accepted/rejected) and process all others
      const finalMappings = columnMappings
        .filter(m => !m.suggested || m.accepted)
        .map(m => {
          // Rejected mappings use source_column as their standalone target
          const targetCol = m.match_type === 'rejected' ? m.source_column : m.target_column;
          const selectedDataType = columnDataTypes[targetCol]?.selected || m.data_type;
          
          return {
            source_file_id: m.source_file_id,
            source_column: m.source_column,
            target_column: targetCol,
            data_type: selectedDataType,
            is_key: m.is_key || deduplicationKeys.includes(targetCol),
            is_indexable: m.is_indexable || indexableColumns.includes(targetCol)
          };
        });

      const request = {
        session_id: session.session_id,
        column_mappings: finalMappings,
        deduplication_keys: deduplicationKeys,
        create_indexes: indexableColumns
      };

      // Use different endpoint for version updates
      const endpoint = isVersionUpdate
        ? `/api/cdp/sessions/${session.session_id}/process-version`
        : `/api/cdp/sessions/${session.session_id}/unify`;

      const response = await apiCall(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      });

      if (response.ok) {
        alert(isVersionUpdate 
          ? `Version ${nextVersion} processing started! This may take a few minutes.`
          : 'Data unification started! This may take a few minutes.'
        );
        onRefresh();
      }
    } catch (error) {
      console.error('Error unifying data:', error);
      alert('Failed to start data unification');
    } finally {
      setProcessing(false);
    }
  };

  // Group mappings by target column for display
  // Rejected mappings appear under their own source_column name (not the suggested target)
  const groupedMappings = columnMappings.reduce((acc, mapping, index) => {
    const key = mapping.match_type === 'rejected' ? mapping.source_column : mapping.target_column;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push({ ...mapping, mappingIndex: index });
    return acc;
  }, {});

  const targetColumns = Object.keys(groupedMappings).sort();

  // Count pending suggestions
  const pendingSuggestions = columnMappings.filter(m => m.suggested && !m.accepted).length;
  
  // Count rejected mappings that need review
  const rejectedMappings = columnMappings.filter(m => m.match_type === 'rejected').length;

  return (
    <div className="space-y-6">

      {/* Show locked view if already unified */}
      {session?.status === 'ready' && session?.unified_at && !isVersionUpdate ? (
        <UnifiedSchemaView session={session} files={files} />
      ) : (
        /* Editable unification panel */
        <>
      {/* Column Analysis Warning */}
      {false && <ColumnMatchingWarning analysis={columnAnalysis} />}

      {/* Version Update Banner */}
      {isVersionUpdate && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Upload className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-purple-900">
                Version {nextVersion} Update - Adding New Data
              </h3>
              <div className="text-xs text-purple-800 mt-1 space-y-1">
                <p>• Deduplication keys and indexes are <strong>locked</strong> from Version {session.current_version}</p>
                <p>• New columns will be added to the existing schema</p>
                <p>• Existing customers will be updated where keys match</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Unification Panel */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isVersionUpdate ? `Version ${nextVersion} - Column Mapping` : 'Data Unification'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {isVersionUpdate 
                  ? 'Map new columns to existing schema or create new columns'
                  : 'Review and adjust column mappings before unifying your data'
                }
              </p>
            </div>
            <button
              onClick={analyzeColumns}
              disabled={analyzing}
              className="flex items-center space-x-2 px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${analyzing ? 'animate-spin' : ''}`} />
              <span>Re-analyze</span>
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="grid grid-cols-5 gap-4">
            <div>
              <p className="text-xs text-gray-600">Total Columns</p>
              <p className="text-xl font-bold text-gray-900">{targetColumns.length}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Dedup Keys</p>
              <div className="flex items-center space-x-1">
                <p className="text-xl font-bold text-purple-600">{deduplicationKeys.length}</p>
                {isVersionUpdate && lockedDeduplicationKeys.length > 0 && (
                  <Lock className="h-4 w-4 text-purple-600" />
                )}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-600">Exact Matches</p>
              <p className="text-xl font-bold text-green-600">
                {columnMappings.filter(m => m.match_type === 'exact').length}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Pending Suggestions</p>
              <p className="text-xl font-bold text-yellow-600">{pendingSuggestions}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600">{isVersionUpdate ? 'New Columns' : 'Unique Columns'}</p>
              <p className="text-xl font-bold text-gray-600">
                {columnMappings.filter(m => isVersionUpdate ? m.match_type === 'new' : m.match_type === 'unique').length}
              </p>
            </div>
          </div>
        </div>

        {/* Merge Strategy Info Box */}
        {!isVersionUpdate && (
          <div className="px-6 py-4 bg-blue-50 border-b hidden">
            <div className="flex items-start space-x-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">
                  Merge Strategy: {deduplicationKeys.length > 0 ? 'MERGE + DEDUPLICATE' : 'UNION ALL (Vertical Stack)'}
                </h3>
                {deduplicationKeys.length > 0 ? (
                  <div className="text-xs text-blue-800 space-y-1">
                    <p>✓ Files will be <strong>merged horizontally</strong> and deduplicated on: <strong>{deduplicationKeys.join(', ')}</strong></p>
                    <p>✓ Multiple rows for the same key will be <strong>merged into one</strong></p>
                    <p>✓ Result: Unique records with combined attributes from all files</p>
                  </div>
                ) : (
                  <div className="text-xs text-blue-800 space-y-1">
                    <p>⚠ No deduplication keys selected - all rows from all files will be <strong>stacked vertically</strong></p>
                    <p>⚠ This may result in duplicate customer records across files</p>
                    <p>💡 <strong>Tip:</strong> Check the "Key" checkbox on columns like customer_id or email to enable deduplication</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Pending Suggestions Section */}
        {pendingSuggestions > 0 && (
          <div className="px-6 py-4 bg-yellow-50 border-b">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-yellow-900">
                  {pendingSuggestions} Suggested Mapping{pendingSuggestions > 1 ? 's' : ''} Pending Review
                </h3>
                <p className="text-xs text-yellow-800 mt-1">
                  Review the fuzzy matches below and accept or reject each suggestion
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Column Mappings Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Target Column
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Source Mappings
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Match Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Data Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Options
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {targetColumns.map((targetCol) => {
                const mappings = groupedMappings[targetCol];
                const firstMapping = mappings[0];
                const hasSuggestions = mappings.some(m => m.suggested);
                const hasRejected = mappings.some(m => m.match_type === 'rejected');
                const isKeyLocked = isVersionUpdate && lockedDeduplicationKeys.includes(targetCol);
                const isIndexLocked = isVersionUpdate && lockedIndexableColumns.includes(targetCol);
                
                return (
                  <tr key={targetCol} className={`hover:bg-gray-50 ${
                    hasSuggestions ? 'bg-yellow-50' : hasRejected ? 'bg-red-50' : ''
                  }`}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      <div className="flex items-center space-x-2">
                        <span>{targetCol}</span>
                        {hasSuggestions && (
                          <AlertCircle className="h-4 w-4 text-yellow-500" />
                        )}
                        {hasRejected && (
                          <X className="h-4 w-4 text-red-500" />
                        )}
                        {isKeyLocked && (
                          <Lock className="h-3 w-3 text-purple-500" title="Locked deduplication key" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="space-y-1">
                        {mappings.map((mapping, idx) => {
                          const file = files.find(f => f.file_id === mapping.source_file_id);
                          const isRemoved = mapping.removed;
                          const canRestore = isRemoved && mappings.length === 1; // Only show restore if this is a single removed mapping
                          
                          return (
                            <div key={idx} className="flex items-center justify-between space-x-2 group">
                              <div className="flex items-center space-x-2 flex-1 min-w-0">
                                {mapping.suggested ? (
                                  <AlertTriangle className="h-3 w-3 text-yellow-500 flex-shrink-0" />
                                ) : mapping.match_type === 'rejected' ? (
                                  <X className="h-3 w-3 text-red-500 flex-shrink-0" />
                                ) : mapping.match_type === 'exact' ? (
                                  <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                                ) : mapping.match_type === 'new' ? (
                                  <Plus className="h-3 w-3 text-blue-500 flex-shrink-0" />
                                ) : (
                                  <Info className="h-3 w-3 text-gray-400 flex-shrink-0" />
                                )}
                                <span className="text-gray-600 text-xs truncate">
                                  {file?.filename}: <span className="font-medium">{mapping.source_column}</span>
                                </span>
                                {mapping.confidence < 1.0 && (
                                  <span className="text-xs text-gray-500">
                                    ({Math.round(mapping.confidence * 100)}%)
                                  </span>
                                )}
                              </div>
                              
                              {/* Individual Remove/Undo buttons */}
                              {!isRemoved && mappings.filter(m => !m.removed).length > 1 && (
                                <button
                                  onClick={() => removeMappingFromGroup(mapping.mappingIndex)}
                                  className="opacity-0 group-hover:opacity-100 p-1 text-red-600 hover:bg-red-50 rounded transition-all flex-shrink-0"
                                  title="Remove this mapping from this target column"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="space-y-1">
                        {mappings.map((mapping, idx) => (
                          <div key={idx}>
                            {mapping.suggested ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Suggested
                              </span>
                            ) : mapping.match_type === 'exact' ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Exact
                              </span>
                            ) : mapping.match_type === 'fuzzy' ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Fuzzy
                              </span>
                            ) : mapping.match_type === 'rejected' ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Rejected - Review Needed
                              </span>
                            ) : mapping.match_type === 'new' ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                New Column
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                Unique
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="space-y-1">
                        <select
                          value={columnDataTypes[targetCol]?.selected || 'string'}
                          onChange={(e) => handleDataTypeChange(targetCol, e.target.value)}
                          className={`w-full px-2 py-1 border rounded text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            getTypeColor(columnDataTypes[targetCol]?.selected || 'string')
                          }`}
                        >
                          {availableDataTypes.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                        {columnDataTypes[targetCol]?.suggested && 
                         columnDataTypes[targetCol]?.suggested !== columnDataTypes[targetCol]?.selected && (
                          <div className="text-xs text-gray-500 italic">
                            Suggestion: {formatDataType(columnDataTypes[targetCol].suggested)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-3">
                        <label className="flex items-center space-x-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={deduplicationKeys.includes(targetCol)}
                            onChange={() => toggleDeduplicationKey(targetCol)}
                            disabled={isKeyLocked}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <span className="text-xs text-gray-600 flex items-center space-x-1">
                            <span>Key</span>
                            {isKeyLocked && <Lock className="h-3 w-3 text-purple-500" />}
                          </span>
                        </label>
                        <label className="flex items-center space-x-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={indexableColumns.includes(targetCol)}
                            onChange={() => toggleIndexableColumn(targetCol)}
                            disabled={isIndexLocked}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <span className="text-xs text-gray-600 flex items-center space-x-1">
                            <span>Index</span>
                            {isIndexLocked && <Lock className="h-3 w-3 text-blue-500" />}
                          </span>
                        </label>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        {hasSuggestions && (
                          <>
                            <button
                              onClick={() => {
                                mappings.forEach(m => {
                                  if (m.suggested) {
                                    acceptSuggestedMapping(m.mappingIndex);
                                  }
                                });
                              }}
                              className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                              title="Accept all suggestions for this column"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                mappings.forEach(m => {
                                  if (m.suggested) {
                                    rejectSuggestedMapping(m.mappingIndex);
                                  }
                                });
                              }}
                              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Reject all suggestions for this column"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setEditingMapping(targetCol)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Edit mapping"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Removed Mappings Section */}
        {columnMappings.filter(m => m.removed).length > 0 && (
          <div className="px-6 py-4 border-t bg-orange-50">
            <div className="flex items-start space-x-3 mb-3">
              <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-orange-900">
                  Removed Mappings ({columnMappings.filter(m => m.removed).length})
                </h4>
                <p className="text-xs text-orange-800 mt-1">
                  These mappings have been removed from their target columns. Click the undo button to restore them.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              {columnMappings.filter(m => m.removed).map((mapping, idx) => {
                const file = files.find(f => f.file_id === mapping.source_file_id);
                const availableTargets = [...new Set(columnMappings.filter(m => !m.removed).map(m => m.target_column))].sort();
                
                return (
                  <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg border border-orange-200">
                    <div className="flex items-center space-x-3 flex-1">
                      <Info className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm">
                          <span className="text-gray-600">{file?.filename}:</span>{' '}
                          <span className="font-medium text-gray-900">{mapping.source_column}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          Removed from: <span className="font-medium">{mapping.originalTargetColumn || 'previous group'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <select
                        value=""
                        onChange={(e) => {
                          if (e.target.value) {
                            restoreMappingToGroup(mapping.mappingIndex, e.target.value);
                          }
                        }}
                        className="text-xs border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Add to target...</option>
                        {availableTargets.map(target => (
                          <option key={target} value={target}>{target}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="px-6 py-4 border-t bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {pendingSuggestions > 0 ? (
                <span className="text-yellow-700 font-medium">
                  ⚠️ Please review {pendingSuggestions} pending suggestion{pendingSuggestions > 1 ? 's' : ''} before {isVersionUpdate ? 'processing version' : 'unifying'}
                </span>
              ) : rejectedMappings > 0 ? (
                <span className="text-blue-700 font-medium">
                  ℹ️ {rejectedMappings} rejected mapping{rejectedMappings > 1 ? 's' : ''} will map to their source column names
                </span>
              ) : (
                <span className="text-green-700 font-medium">
                  ✓ All mappings reviewed - ready to {isVersionUpdate ? 'process version' : 'unify'}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              {!isVersionUpdate && (
                <button
                  onClick={() => {
                    // Accept all suggestions
                    columnMappings.forEach((m, idx) => {
                      if (m.suggested) {
                        acceptSuggestedMapping(idx);
                      }
                    });
                  }}
                  disabled={pendingSuggestions === 0}
                  className="px-4 py-2 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Accept All Suggestions
                </button>
              )}
              <button
                onClick={handleUnifyData}
                disabled={processing || pendingSuggestions > 0 || columnMappings.length === 0}
                className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="h-5 w-5" />
                <span>{processing ? 'Processing...' : isVersionUpdate ? `Process Version ${nextVersion}` : 'Unify Data'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mapping Editor Modal */}
      {editingMapping && (
        <MappingEditorModal
          targetColumn={editingMapping}
          mappings={groupedMappings[editingMapping]}
          files={files}
          allTargetColumns={targetColumns}
          onClose={() => setEditingMapping(null)}
          onSave={(updatedMappings) => {
            const newMappings = [...columnMappings];
            updatedMappings.forEach(updated => {
              newMappings[updated.originalIndex] = updated;
            });
            setColumnMappings(newMappings);
            setEditingMapping(null);
          }}
        />
      )}
      </>
      )}
    </div>
  );
};

export {MappingEditorModal, EnhancedUnifyTab};